"use client";
import React, {
  ReactNode,
  useContext,
  useState,
  useEffect,
  createContext,
} from "react";
import {
  CssBaseline,
  ThemeProvider as MuiThemeProvider,
  PaletteMode,
} from "@mui/material";
import paletteBlue from "./palette/paletteBlue";
import paletteYellow from "./palette/paletteYellow";
import paletteGreen from "./palette/paletteGreen";
import paletteRed from "./palette/paletteRed";
import createCustomTheme from "./createTheme";
import ComponentOverrides from "./overrides/componentOverrides";

interface ThemeContextType {
  mode: PaletteMode;
  toggleMode: () => void;
  setThemeColor: (color: string) => void;
}

const ThemeContext = createContext<ThemeContextType>({
  mode: "light",
  toggleMode: () => { },
  setThemeColor: () => { },
});

const getPalette = (color: string) => {
  switch (color) {
    case "yellow":
      return paletteYellow;
    case "green":
      return paletteGreen;
    case "red":
      return paletteRed;
    case "blue":
    default:
      return paletteBlue;
  }
};

const ThemeProvider = ({
  children,
  sMode,
  sTheme,
}: {
  children: ReactNode;
  sTheme?: string;
  sMode?: PaletteMode;
}) => {
  const [mode, setMode] = useState<PaletteMode>(sMode || 'light');
  const [palette, setPalette] = useState(getPalette(sTheme || "blue"));

  useEffect(() => {
    if (document) {
      const cookies = document.cookie
        .split("; ")
        .reduce((prev: any, current) => {
          const [name, ...value] = current.split("=");
          prev[name] = value.join("=");
          return prev;
        }, {});

      const savedMode = (
        cookies["mode"] === "dark" ? "dark" : "light"
      ) as PaletteMode;
      const savedPalette = cookies["theme"] || "blue";

      if (savedMode) setMode(savedMode);
      if (savedPalette) setPalette(getPalette(savedPalette));
    }
  }, []);

  const toggleMode = () => {
    setMode((prevMode) => {
      const newMode = prevMode === "light" ? "dark" : "light";
      document.cookie = `mode=${newMode}`;
      return newMode;
    });
  };

  const setThemeColor = (color: string) => {
    document.cookie = `theme=${color}`;
    setPalette(getPalette(color));
  };

  const theme = createCustomTheme(mode, palette);
  theme.components = ComponentOverrides(theme);

  return (
    <ThemeContext.Provider value={{ mode, toggleMode, setThemeColor }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

const useMode = () => useContext(ThemeContext);
export { ThemeProvider, useMode };
